import type { ResultOf } from '@graphql-typed-document-node/core'
import { useCallback, useMemo, useRef } from 'react'
import type { FC } from 'react'
import { Autoplay } from 'swiper/modules'
import { Swiper, type SwiperClass, SwiperSlide } from 'swiper/react'
import Image from '~/components/image/Image'
import { Images as ImagesComponent } from '~/components/images'
import { PPG_DEFAULT_IMAGE_URL } from '~/lib/constants/constants'
import { getFragmentData } from '~/lib/graphql'
import type { FragmentType } from '~/lib/graphql'
import type { RestaurantId } from '~/lib/graphql/scalars'
import { Link } from '~/navigation'
import { css, cva, sva } from '~/styled-system/css'
import { grid, hstack } from '~/styled-system/patterns'
import { OverlayScrollButton } from '~/ui/IconButton'
import { RestaurantPhotos } from './RestaurantPhotos.frag'
import type { Images } from './RestaurantPhotos.frag'

type Props = {
  restaurant: FragmentType<typeof RestaurantPhotos>
}

export const SdGuideTitleImage: FC<Props> =
  import.meta.env.VITE_MODE === 'ppg' ? PpgSdGuideTitleImage : IkyuSdGuideTitleImage

export const PcGuideTitleImage: FC<Props> =
  import.meta.env.VITE_MODE === 'ppg' ? PpgPcGuideTitleImage : IkyuPcGuideTitleImage

const defaultImage: Images[number] = {
  url: PPG_DEFAULT_IMAGE_URL,
  name: 'default',
  category: 'GUIDE_GALLERY',
}

function PpgSdGuideTitleImage({ restaurant }: Props) {
  const [restaurantName, guideGallery] = useMemo(() => {
    return ppgImages(restaurant)
  }, [restaurant])

  return (
    <div className="relative w-full">
      <Swiper className="!px-3" slidesPerView={1.3}>
        {guideGallery.map((image, index) => (
          <SwiperSlide key={image.url} className="pr-3 last:pr-0">
            <meta itemProp="image" content={image.url} />
            <Image
              key={image.url}
              src={image.url}
              alt={`${restaurantName} - 画像${index + 1}`}
              className="w-full rounded-xl"
              width={300}
              height={200}
              usePlaceholder={true}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  )
}

function ppgImages(restaurant: FragmentType<typeof RestaurantPhotos>) {
  const { images, name, restaurantId } = getFragmentData(RestaurantPhotos, restaurant)
  const filtered = images.filter((i) => i.category === 'GUIDE_GALLERY')
  const count = filtered.length >= 3 ? 5 : (2 as const)
  const result =
    filtered.length < count
      ? filtered.concat(Array(count - filtered.length).fill(defaultImage))
      : filtered.slice(0, count)
  return [name, result, count, restaurantId] as const
}

function IkyuSdGuideTitleImage({ restaurant }: Props) {
  const { images, name: restaurantName } = getFragmentData(RestaurantPhotos, restaurant)
  const guideGallery = images.map((i) => ({ url: i.url, name: `${restaurantName}-画像` }))

  return (
    <div className="relative w-full">
      <ImagesComponent images={guideGallery} width={700} height={350} dot="dynamic" fraction />
      {guideGallery.map((image) => (
        <meta itemProp="image" content={image.url} key={image.url} />
      ))}
    </div>
  )
}

function PpgPcGuideTitleImage({ restaurant }: Props) {
  const [name, images, count, restaurantId] = ppgImages(restaurant)
  if (count === 5) {
    return <PpgPcGuideTitleImage5 restaurantId={restaurantId} name={name} images={images} />
  }
  return <PpgPcGuideTitleImage2 restaurantId={restaurantId} name={name} images={images} />
}

const ppgImgStyle = cva({
  base: {
    w: '[100%]',
  },
  variants: {
    position: {
      left: {
        borderLeftRadius: 'xl',
      },
      right: {
        borderRightRadius: 'xl',
      },
      topRight: {
        borderTopRightRadius: 'xl',
      },
      bottomRight: {
        borderBottomRightRadius: 'xl',
      },
    },
  },
})

function PpgPcGuideTitleImage5({
  restaurantId,
  name,
  images,
}: {
  restaurantId: RestaurantId
  name: string
  images: ResultOf<typeof RestaurantPhotos>['images']
}) {
  return (
    <div
      className={hstack({
        mx: 'auto',
        gap: '1',
        w: '[1000px]',
        h: '[300px]',
      })}
    >
      <Link to={`/${restaurantId}/photos`} key={`${restaurantId}-images-0-${images[0].url}`}>
        <Image
          src={images[0].url}
          alt={`${name} - 画像1`}
          className={ppgImgStyle({ position: 'left' })}
          width={498}
          height={300}
          usePlaceholder={true}
        />
      </Link>
      <div className={grid({ columns: 2, gap: '1' })}>
        {images.slice(1, 5).map((image, index) => (
          <Link
            to={`/${restaurantId}/photos`}
            key={`${restaurantId}-images-${index + 1}-${images[0].url}`}
          >
            <Image
              src={image.url}
              alt={`${name} - 画像${index + 2}`}
              className={ppgImgStyle({
                position: index === 1 ? 'topRight' : index === 3 ? 'bottomRight' : undefined,
              })}
              width={246}
              height={148}
              usePlaceholder={true}
            />
          </Link>
        ))}
      </div>
    </div>
  )
}

function PpgPcGuideTitleImage2({
  restaurantId,
  name,
  images,
}: {
  restaurantId: RestaurantId
  name: string
  images: ResultOf<typeof RestaurantPhotos>['images']
}) {
  return (
    <div
      className={hstack({
        mx: 'auto',
        gap: '1',
        w: '[1000px]',
        h: '[300px]',
      })}
    >
      {images.slice(0, 2).map((image, index) => (
        <Link to={`/${restaurantId}/photos`} key={`${image.url}-${index}`}>
          <Image
            src={image.url}
            alt={`${name} - 画像${index + 1}`}
            className={ppgImgStyle({ position: index === 0 ? 'left' : 'right' })}
            width={498}
            height={300}
            usePlaceholder={true}
          />
        </Link>
      ))}
    </div>
  )
}

const styles = sva({
  slots: ['container'],
  base: {
    container: {
      display: 'flex!',
      w: '[800px!]',
      h: '[400px!]',
    },
  },
})

function IkyuPcGuideTitleImage({ restaurant }: Props) {
  const { images, name: restaurantName } = getFragmentData(RestaurantPhotos, restaurant)

  const guideGallery = useMemo(() => {
    const filtered = images.filter((i) => i.category === 'GUIDE_GALLERY')
    // images が空の場合を考慮しないと、ハイドレーションエラーが発生する
    if (filtered.length === 0) {
      return []
    }
    // 2枚～4枚のときに表示が崩れるため、5枚以上になるように調整
    // TODO: 暫定対応のため Swiper の扱い方を根本的に直す必要あり
    // @see https://github.com/ikyucom/macaron-frontend/issues/4743
    const repeatCount = Math.ceil(5 / filtered.length)
    return Array.from({ length: repeatCount }).flatMap(() => filtered)
  }, [images])

  const classes = styles()

  // Navigation module (https://swiperjs.com/swiper-api#navigation)
  // が効かないので明示的に操作
  const swiper = useRef<SwiperClass | null>(null)
  const next = useCallback(() => swiper.current?.slideNext(), [])
  const prev = useCallback(() => swiper.current?.slidePrev(), [])

  return (
    <div className="relative w-full">
      <Swiper
        modules={[Autoplay]}
        // res2 に準拠してガイドのスライド時間は 4000ms
        // https://github.com/ikyucom/restaurant2/blob/27c5537760edb08e9fd3ea541207693cc34b018c/nuxt/components/templates/pages/~guide/index.default.vue#L18
        autoplay={{ delay: 4000, disableOnInteraction: true }}
        slidesPerView="auto"
        centeredSlides={true}
        updateOnWindowResize={true}
        loop={true}
        onSwiper={(s) => (swiper.current = s)}
        role="group"
      >
        {guideGallery.map((image, index) => {
          return (
            <SwiperSlide key={`${image.url}-${index}`} className={classes.container}>
              <meta itemProp="image" content={image.url} />
              <Image
                src={image.url}
                alt={`${restaurantName} - 画像${index + 1}`}
                className={css({ w: '[100%]' })}
                width={800}
                height={400}
                usePlaceholder={true}
              />
            </SwiperSlide>
          )
        })}
        <OverlayScrollButton onClick={prev} direction="left" />
        <OverlayScrollButton onClick={next} direction="right" />
      </Swiper>
    </div>
  )
}
